import { Table } from 'antd'
import { ColumnProps } from 'antd/lib/table'
import { FC, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { PerEmpty } from '../../../components/custom_components/Empty'
import { PerTableActionButtons } from '../../../components/custom_components/TableActionButtons'
import { tableActionButtons } from '../../../components/custom_components/TableActionButtons/tableActionButtons.uiprops'
import { ReportFragment, ReportStatus } from '../../../types'
import { useAuth } from '../../../context/auth-context'
import { useDateTime } from '../../../shared/useDateTime'
import { noFilterOption, useReportStore } from '../ReportStore'
import { IconIndicator } from '../../../components/custom_components/IconIndicator/IconIndicator'
import BackgroundBox from '../../../components/custom_components/BackgroundBox'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import PerText from '../../../components/custom_components/Text'
import PerButtonCircle from '../../../components/custom_components/ButtonCircle'
import classNames from 'classnames'

interface MeetingReportsTableProps {
  reports: ReportFragment[]
  showMoreButtonVisible: boolean
}

export const MeetingReportsTable: FC<MeetingReportsTableProps> = ({ reports, showMoreButtonVisible }) => {
  const [t] = useTranslation()
  const { user } = useAuth()
  const { formatDateShort } = useDateTime(user)
  const { tableActionButtonsRowWidth } = tableActionButtons()
  const [disclaimerVisible, setDisclaimerVisible] = useState(true)

  const [selectedCountry] = useReportStore((state) => [state.selectedCountry])

  const columns: ColumnProps<ReportFragment>[] = [
    {
      key: 'fileName',
      title: t('reports.table.filename'),
      className: 'minWidth-column-xl textAlign-left',
      sorter: (reportA: ReportFragment, reportB: ReportFragment) => {
        return reportA.filename.localeCompare(reportB.filename)
      },
      render: (rowData: ReportFragment) => <>{rowData.filename}</>,
    },
    {
      key: 'type',
      title: t('reports.table.type'),
      className: 'minWidth-column-l textAlign-left',
      sorter: (reportA: ReportFragment, reportB: ReportFragment) => {
        return reportA.type.localeCompare(reportB.type)
      },
      render: (rowData: ReportFragment) => <>{t(`reports.type.${rowData.type}`)}</>,
    },
    {
      key: 'country',
      title: t('reports.table.country'),
      className: 'minWidth-column-m textAlign-left',
      sorter: (reportA: ReportFragment, reportB: ReportFragment) => {
        return reportA.country.localeCompare(reportB.country)
      },
      render: (rowData: ReportFragment) => <>{rowData.country}</>,
    },
    {
      key: 'size',
      title: t('reports.table.size'),
      className: 'minWidth-column-s textAlign-left',
      sorter: (reportA: ReportFragment, reportB: ReportFragment) => {
        return reportA.size - reportB.size
      },
      render: (rowData: ReportFragment) => <>{rowData.size}</>,
    },
    {
      key: 'format',
      title: t('reports.table.format'),
      className: 'minWidth-column-m textAlign-left',
      sorter: (reportA: ReportFragment, reportB: ReportFragment) => {
        return reportA.format.localeCompare(reportB.format)
      },
      render: (rowData: ReportFragment) => <>{rowData.format}</>,
    },
    {
      key: 'date',
      title: t('reports.table.date'),
      className: 'minWidth-column-m textAlign-left',
      sorter: (reportA: ReportFragment, reportB: ReportFragment) => {
        return new Date(reportA.date).getTime() - new Date(reportB.date).getTime()
      },
      render: (rowData: ReportFragment) => <>{formatDateShort(rowData.date.toString())}</>,
    },
    {
      key: 'actions',
      title: t('reports.table.actions'),
      className: 'textAlign-left',
      width: tableActionButtonsRowWidth(1),
      render: (rowData: ReportFragment) => (
        <PerTableActionButtons
          variant="pastel"
          buttons={[
            {
              icon: 'download_fill',
              color: 'primary',
              asLink: rowData.status === ReportStatus.Processed ? rowData.downloadLink : undefined,
              tooltip: rowData.status === ReportStatus.Processed ? t('reports.table.downloadTooltip') : t('reports.table.errorTooltip'),
              disabled: rowData.status !== ReportStatus.Processed,
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      {disclaimerVisible && <BetaVersionDisclaimer onClose={() => setDisclaimerVisible(false)} />}
      <Table
        className={classNames('light small scroll-compensation', {
          viewWithTabs: !disclaimerVisible && !showMoreButtonVisible,
          viewWithTabsAndMessageBox: disclaimerVisible && !showMoreButtonVisible,
          viewWithTabsAndShowMoreButton: !disclaimerVisible && showMoreButtonVisible,
          viewWithTabsAndMessageBoxAndShowMoreButton: disclaimerVisible && showMoreButtonVisible,
        })}
        dataSource={reports.map((data) => ({ ...data, key: data.id }))}
        columns={columns}
        pagination={false}
        scroll={{ y: true }}
        locale={{
          emptyText: (
            <PerEmpty
              icon="noReport_stroke"
              heading={
                !selectedCountry || selectedCountry.label === noFilterOption.value
                  ? t('reports.table.empty')
                  : t('reports.table.filteredEmpty')
              }
            />
          ),
        }}
      />
    </>
  )
}

const BetaVersionDisclaimer: FC<{
  onClose: () => void
}> = ({ onClose }) => {
  return (
    <BackgroundBox mb="xxs" r height="messageBox" noPadding color="white">
      <PerFlexRow align="middle" noWrap className="fullHeight" justify="space-between" mr="m" ml="m">
        <PerFlexRow align="middle">
          <PerFlexRow width="5%">
            <IconIndicator icon="warning_fill" background="warning" />
          </PerFlexRow>
          <PerFlexRow width="90%">
            <PerText fontSize="s">
              <Trans i18nKey="reports.table.meetingReportBetaVersionDisclaimer" components={[<b></b>]} />
            </PerText>
          </PerFlexRow>
        </PerFlexRow>

        <PerButtonCircle size="large" icon="close_stroke" onClick={onClose} />
      </PerFlexRow>
    </BackgroundBox>
  )
}
